<template>
  <div class="col-12 col-xl-12">
    <div class="container-fluid px-0">
      <!-- Unified Filters Row -->
      <div class="row mb-4 g-2" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
        <!-- Academic Year Filter -->
        <div class="col-6 col-md-3 col-lg-3">
          <select
            class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
            :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
            v-model="filters.academicYearId"
            @change="loadHistory"
          >
            <option value="">{{ $t('promotion.all_years') }}</option>
            <option
              v-for="year in academicYears"
              :key="year.id"
              :value="year.id"
            >
              {{ year.name }} ({{ year.year }})
              <span v-if="year.is_active"> - {{ $t('common.current') }}</span>
            </option>
          </select>
        </div>

        <!-- Class Filter -->
        <div class="col-6 col-md-3 col-lg-3">
          <select
            class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
            :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
            v-model="filters.classId"
            @change="loadHistory"
          >
            <option value="">{{ $t('promotion.all_classes') }}</option>
            <option
              v-for="classItem in classes"
              :key="classItem.id"
              :value="classItem.id"
            >
              {{ classItem.name }}
            </option>
          </select>
        </div>

        <!-- Reset button (desktop) -->
        <div class="col-md-1 d-none d-md-block">
          <button
            class="btn btn-outline-secondary btn-sm w-100 shadow-xs"
            @click="resetFilters"
            :disabled="loading"
            :title="$t('promotion.reset_filters')"
          >
            <i class="fas fa-filter-circle-xmark"></i>
          </button>
        </div>

        <!-- Search by student name -->
        <div class="col-12 col-md-5 col-lg-5 position-relative">
          <input
            type="text"
            class="form-control form-control-sm border border-2 border-gray-300 shadow-xs"
            :class="locale === 'ar' ? 'ps-5 text-end' : 'pe-5 text-start'"
            v-model="filters.studentName"
            :placeholder="$t('promotion.search_student_placeholder')"
@input="debouncedStudentSearch"
  @keyup.enter="loadHistory"

            :dir="locale === 'ar' ? 'rtl' : 'ltr'"
          />
          <span
            class="position-absolute top-50 translate-middle-y text-muted"
            :class="locale === 'ar' ? 'start-0 ps-3' : 'end-0 pe-3'"
          >
            <i class="fas fa-search"></i>
          </span>
        </div>

        <!-- Reset button (mobile) -->
        <div class="col-12 d-md-none mt-2">
          <button
            class="btn btn-outline-secondary btn-sm w-100 shadow-xs"
            @click="resetFilters"
            :disabled="loading"
          >
            <i class="fas fa-filter-circle-xmark me-1"></i>
            {{ $t('promotion.reset_filters') }}
          </button>
        </div>
      </div>
    </div>


    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3">{{ $t('promotion.history_title') }}</h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2">
        <div class="table-responsive p-2 p-md-4">
          <LoadingSpinner v-if="loading" :message="$t('promotion.loading_history')" />
          
          <template v-else>
            <div class="d-none d-xl-block">
              <div class="table-responsive">
                <table class="table align-items-center mb-0" style="width: 100%">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 20%">{{ $t('students.name') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 10%">{{ $t('academic.year') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 15%">{{ $t('students.class') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 15%">{{ $t('students.group') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 15%">{{ $t('promotion.promotion_type') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 10%">{{ $t('common.status') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 10%">{{ $t('common.start_date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="record in promotionHistory" :key="record.id">
                      <td>
                        <div class="d-flex flex-column">
                          <span class="text-sm font-weight-bold">
                            {{ record.student?.full_name || 
                               `${record.student?.first_name || ''} ${record.student?.father_name || ''} ${record.student?.last_name || ''}`.trim() 
                            }}
                          </span>
                          <small class="text-xs text-muted">ID: {{ record.student_id }}</small>
                        </div>
                      </td>
                      <td>
                        <span class="text-sm">{{ record.academic_year?.name || 'N/A' }}</span>
                      </td>
                      <td>
                        <span class="text-sm">{{ record.class?.name || 'N/A' }}</span>
                      </td>
                      <td>
                        <span class="text-sm">{{ record.class_group?.group?.name || 'N/A' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-sm" :class="getPromotionTypeBadge(record.promotion_type)">
                          {{ getPromotionTypeDisplay(record.promotion_type, record.notes) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-sm" :class="statusBadgeClass(record.status)">
                          {{ $t(`students.status_${record.status}`) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="text-sm">{{ formatDate(record.start_date) }}</span>
                      </td>
                    </tr>
                    <tr v-if="promotionHistory.length === 0">
                      <td colspan="8" class="text-center py-4 text-muted">
                        <i class="fas fa-history fa-2x mb-2"></i>
                        <p>{{ $t('promotion.no_history_found') }}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div class="d-xl-none">
              <div v-if="promotionHistory.length === 0" class="text-center py-4 text-muted">
                <i class="fas fa-history fa-2x mb-2"></i>
                <p>{{ $t('promotion.no_history_found') }}</p>
              </div>
              
              <div v-else class="row g-3">
                <div class="col-12" v-for="record in promotionHistory" :key="record.id">
                  <div class="card shadow-xs border">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <strong class="text-sm">
                          {{ record.student?.full_name || 
                             `${record.student?.first_name || ''} ${record.student?.father_name || ''} ${record.student?.last_name || ''}`.trim() 
                          }}
                        </strong>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ $t('academic.year') }}:</span>
                        <span class="text-sm">{{ record.academic_year?.name || 'N/A' }}</span>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ $t('students.class') }}:</span>
                        <span class="text-sm">{{ record.class?.name || 'N/A' }}</span>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ $t('students.group') }}:</span>
                        <span class="text-sm">{{ record.class_group?.group?.name || 'N/A' }}</span>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ $t('promotion.promotion_type') }}:</span>
                        <span class="badge badge-sm" :class="getPromotionTypeBadge(record.promotion_type)">
                          {{ getPromotionTypeDisplay(record.promotion_type, record.notes) }}
                        </span>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ $t('common.status') }}:</span>
                        <span class="badge badge-sm" :class="statusBadgeClass(record.status)">
                          {{ $t(`students.status_${record.status}`) }}
                        </span>
                      </div>
                      
                      <div class="d-flex justify-content-between mb-3">
                        <span class="text-sm">{{ $t('common.start_date') }}:</span>
                        <span class="text-sm">{{ formatDate(record.start_date) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import debounce from 'lodash/debounce'

const { t, locale } = useI18n()
const toast = useToast()
const debouncedStudentSearch = debounce(() => {
  loadHistory()
}, 400) 
const academicYears = ref([])
const classes = ref([])
const promotionHistory = ref([])
const loading = ref(false)

const filters = ref({
  academicYearId: '',
  classId: '',
  studentName: ''
})

// Load initial data
const loadAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years')
    academicYears.value = response.data.data || []
  } catch (error) {
    console.error('Error loading academic years:', error)
    toast.error(t('errors.load_failed'))
  }
}

const loadClasses = async () => {
  try {
    const response = await api.get('/classes', { params: { all: true } })
    classes.value = response.data.data || []
  } catch (error) {
    console.error('Error loading classes:', error)
    toast.error(t('errors.load_failed'))
  }
}

const loadHistory = async () => {
  loading.value = true
  try {
    const cleanFilters = {}
    if (filters.value.academicYearId) {
      cleanFilters.academicYearId = filters.value.academicYearId
    }
    if (filters.value.classId) {
      cleanFilters.classId = filters.value.classId
    }
    if (filters.value.studentName && filters.value.studentName.trim() !== '') {
      cleanFilters.student_name = filters.value.studentName.trim()
    }

    const response = await api.get('/student-promotion/history', {
      params: cleanFilters
    })
    
    promotionHistory.value = response.data.data || []
    
  } catch (error) {
    console.error('Error loading promotion history:', error)
    toast.error(error.response?.data?.message || t('errors.load_failed'))
    promotionHistory.value = []
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    academicYearId: '',
    classId: '',
        studentName: ''

  }
  loadHistory()
}

const getPromotionTypeBadge = (type) => {
  const classes = {
    promote: 'bg-success',
    repeat: 'bg-warning',
    transfer: 'bg-info'
  }
  return classes[type] || 'bg-secondary'
}

const getPromotionTypeDisplay = (type, notes) => {
  if (!type) {
    // Try to infer from notes
    if (notes?.includes('ترقية') || notes?.includes('promote')) {
      return t('promotion.promote')
    } else if (notes?.includes('إعادة') || notes?.includes('repeat')) {
      return t('promotion.repeat')
    } else if (notes?.includes('نقل') || notes?.includes('transfer')) {
      return t('promotion.transfer')
    } else {
      return t('promotion.promote') // Default fallback
    }
  }
  return t(`promotion.${type}`)
}

const statusBadgeClass = (status) => {
  const classes = {
    active: 'bg-success',
    completed: 'bg-primary',
    transferred: 'bg-warning'
  }
  return classes[status] || 'bg-secondary'
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString(locale.value)
}

watch(
  () => [filters.value.academicYearId, filters.value.classId],
  () => {
    loadHistory()
  },
  { deep: true }
)

onMounted(() => {
  loadAcademicYears()
  loadClasses()
  loadHistory()
})
</script>